import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import CodeIcon from '@mui/icons-material/Code';
import PauseRounded from '@mui/icons-material/PauseRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import { BaseStyleEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import CommonMessages from '~/core/commonMessages';
import { DasherBoardText, GoalVizEvent } from '~/core/enums';
import { getDataLayer } from '~/core/googleAnalyticsUtils';
import { useShareLinkPopper } from '~/core/hooks/useShareLinkPopper';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { getEnvironmentAwareHref } from '~/core/utils';
import { ActionPopper } from '~/views/components/shareBar/components/styles';

import { PptReplayTeam as Team } from '../../../pptReplayEmbed/types';
import { usePptSpriteQuery } from '../../api';
import { GameCenterTeam, PptEntity, PptSprite } from '../../types';
import Rink from '../rink/Rink';

import {
    ButtonContainer,
    EntityHalo,
    EntityMarker,
    GameCenterButton,
    GameCenterLink,
    LoadingDisplayContainer,
    PptButton,
    PPTReplayContainer,
    RestartButton,
    RestartContainer,
    SweaterNumber,
} from './styles';

type Props = {
    gameId: number;
    gameState: ShortCodeEnums.GameState | null;
    homeTeam: GameCenterTeam | Team;
    awayTeam: GameCenterTeam | Team;
    homeTeamDefendingSide: BaseStyleEnums.Alignments;
    pptReplayUrl?: string;
    goalScorerId?: number;
    dasherBoardText?: DasherBoardText;
    period?: number;
    eventId?: number;
    showEmbedButton?: boolean;
    showGameCenterButton?: boolean;
    isEmbed?: boolean;
};

const MS_PER_FRAME = 100;
const PUCK_ENTITY_ID = 1;
const PUCK_ENTITY_SIZE = 12;
const PLAYER_ENTITY_SIZE = 38;
const MEDIUM_SCREEN_PLAYER_ENTITY_SIZE = 48;
const SMALL_SCREEN_PLAYER_ENTITY_SIZE = 57;

const PPTReplay: React.FC<Props> = ({
    gameId,
    gameState,
    homeTeam,
    awayTeam,
    homeTeamDefendingSide,
    pptReplayUrl,
    goalScorerId,
    dasherBoardText,
    eventId,
    period,
    showGameCenterButton,
    isEmbed = false,
}) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();
    const isSmallScreen = screenSize.width <= BaseStyleEnums.ScreenWidthBreakpoints.small;
    const isSmallerThanMediumScreen = screenSize.width <= BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const playerSize = isSmallScreen
        ? SMALL_SCREEN_PLAYER_ENTITY_SIZE
        : isSmallerThanMediumScreen
        ? MEDIUM_SCREEN_PLAYER_ENTITY_SIZE
        : PLAYER_ENTITY_SIZE;

    const startRef = useRef<number>();
    const timeRef = useRef<number>(0);
    const req = useRef<number>(0);

    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [restart, setRestart] = useState<boolean>(false);
    const [isFinished, setIsFinished] = useState<boolean>(false);

    const [entities, setEntities] = useState<PptSprite>();
    const [nextEntities, setNextEntities] = useState<PptSprite>();
    const [scale, setScale] = useState<number>(0);
    const [timeElapsed, setTimeElapsed] = useState<number>(0);

    const spriteResponse = usePptSpriteQuery(pptReplayUrl);
    const pptData = useMemo(
        () => (spriteResponse?.data && spriteResponse.data.length > 0 ? spriteResponse.data : null),
        [spriteResponse]
    );

    const resetAnimation = () => {
        window.cancelAnimationFrame(req.current);
        setIsPlaying(false);
        setTimeElapsed(0);
        setScale(0);
        timeRef.current = 0;
        startRef.current = undefined;
    };

    useEffect(() => {
        if (pptData) {
            setEntities(pptData[0]);
            setNextEntities(pptData[1]);
            setIsPlaying(true);
        }
    }, [pptData]);

    const step = useCallback(
        (timeStamp: number) => {
            if (pptData) {
                if (startRef.current === undefined) {
                    startRef.current = timeStamp;
                }

                const elapsed = timeStamp - startRef.current;
                timeRef.current = elapsed + timeElapsed;

                const count: number = Math.floor(timeRef.current / MS_PER_FRAME);

                if (count > pptData.length - 2) {
                    setIsFinished(true);
                    return;
                }

                const currentTimeStamp = pptData[count].timeStamp;
                const nextTimeStamp = pptData[count + 1].timeStamp;

                const timeSinceLastDataPoint = elapsed + timeElapsed - count * MS_PER_FRAME;

                // should be 1/10th of a second, but we get the exact interval here
                const timeInterval = (nextTimeStamp - currentTimeStamp) * MS_PER_FRAME;
                // number b/w 0 and 1 to show how far in between the interval we are
                const timeScale = timeSinceLastDataPoint / timeInterval;
                setScale(timeScale);

                if (count < pptData.length - 2) {
                    setEntities(pptData[count]);
                    setNextEntities(pptData[count + 1]);
                    req.current = window.requestAnimationFrame(step);
                }

                if (count === pptData.length - 2) {
                    setIsPlaying(false);
                    setIsFinished(true);
                }
            }
        },
        [pptData, timeElapsed]
    );

    useEffect(() => {
        if (isPlaying) {
            req.current = window.requestAnimationFrame(step);
        } else {
            setTimeElapsed(timeRef.current);
            window.cancelAnimationFrame(req.current);
        }
    }, [isPlaying, step]);

    useEffect(() => {
        if (isFinished) {
            resetAnimation();
        }
    }, [isFinished]);

    useEffect(() => {
        if (restart && pptData) {
            resetAnimation();
            setEntities(pptData[0]);
            setNextEntities(pptData[1]);
            setIsFinished(false);
            setRestart(false);
        }
    }, [pptData, restart]);

    const interpolateCoords = (coord: number, nextCoord: number, scale: number) => {
        return coord + (nextCoord - coord) * scale;
    };

    const embedLink = `https://www.nhl.com${getEnvironmentAwareHref(
        `/ppt-replay/goal/${gameId}/${eventId}`,
        false,
        intl.locale as ShortCodeEnums.Language
    )}`;
    const { handleClick, id, isOpen, anchorElement } = useShareLinkPopper(embedLink);

    return (
        <PPTReplayContainer $isEmbed={isEmbed}>
            <Rink
                gameId={gameId}
                homeTeam={homeTeam}
                awayTeam={awayTeam}
                homeTeamDefendingSide={homeTeamDefendingSide}
                dasherBoardText={dasherBoardText}
                eventId={eventId}
                eventType={GoalVizEvent.GOAL}
                period={period}
            >
                {entities &&
                    nextEntities &&
                    Object.values(entities.onIce).map((entity: PptEntity) => {
                        const nextEntity = nextEntities.onIce[entity.id] || null;
                        const isGoalScorer = goalScorerId === entity.playerId;

                        if (!nextEntity) {
                            return;
                        }

                        const styles = {
                            transform: `translate(${interpolateCoords(
                                entity.x,
                                nextEntity.x,
                                scale
                            )}px, ${interpolateCoords(entity.y, nextEntity.y, scale)}px)`,
                        };

                        const isHome = entity.id !== PUCK_ENTITY_ID && entity.teamId === homeTeam.id;

                        return (
                            entity &&
                            nextEntity && (
                                <g key={entity.id} style={styles}>
                                    <EntityMarker
                                        $isGoalScorer={isGoalScorer}
                                        $isHome={isHome}
                                        $teamAbbrev={entity.teamAbbrev}
                                        r={entity.id === PUCK_ENTITY_ID ? PUCK_ENTITY_SIZE : playerSize}
                                    />
                                    {isGoalScorer && (
                                        <EntityHalo
                                            r={playerSize + 12}
                                            $isHome={isHome}
                                            $teamAbbrev={entity.teamAbbrev}
                                        />
                                    )}
                                    <SweaterNumber $isHome={isHome} $teamAbbrev={entity.teamAbbrev}>
                                        {entity.sweaterNumber}
                                    </SweaterNumber>
                                </g>
                            )
                        );
                    })}
            </Rink>
            {spriteResponse.isLoading && (
                <LoadingDisplayContainer>
                    <LoadingDisplay isInline isLoading size={80} />
                </LoadingDisplayContainer>
            )}
            {isFinished ? (
                <RestartContainer>
                    <RestartButton
                        onClick={() => {
                            getDataLayer().push({
                                event: 'goal_simulation_refresh_click',
                                game_id: gameId,
                                game_state: gameState,
                            });

                            setIsPlaying(true);
                            setIsFinished(false);
                        }}
                    >
                        <ReplayRounded />
                    </RestartButton>
                </RestartContainer>
            ) : (
                <ButtonContainer>
                    <PptButton onClick={() => setRestart(!restart)}>
                        <ReplayRounded />
                    </PptButton>
                    <PptButton
                        onClick={() => {
                            setIsPlaying(!isPlaying);
                            startRef.current = undefined;
                        }}
                    >
                        {isPlaying ? <PauseRounded /> : <PlayArrowRounded />}
                    </PptButton>
                    {eventId && (
                        <>
                            <PptButton
                                title={intl.formatMessage({
                                    description:
                                        "Describes an action that will copy a Goal Visualizer embed code to user's clipboard",
                                    defaultMessage: 'Embed Goal Visualizer',
                                })}
                                onClick={handleClick}
                                aria-describedby={id}
                            >
                                <CodeIcon />
                            </PptButton>
                            <ActionPopper id={id} open={isOpen} anchorEl={anchorElement}>
                                <FormattedMessage
                                    description="A message indicating that a user has copied an embed link to their clipboard"
                                    defaultMessage="Embed Link Copied"
                                />
                            </ActionPopper>
                        </>
                    )}
                    {showGameCenterButton && (
                        <GameCenterLink
                            href={`https://www.nhl.com${getEnvironmentAwareHref(
                                `/gamecenter/${gameId}`,
                                false,
                                intl.locale as ShortCodeEnums.Language
                            )}`}
                            target="_blank"
                        >
                            <GameCenterButton>
                                <FormattedMessage {...CommonMessages.linkDescriptions.gamecenter} />
                            </GameCenterButton>
                        </GameCenterLink>
                    )}
                </ButtonContainer>
            )}
        </PPTReplayContainer>
    );
};

export default PPTReplay;
